import { BOARDS_PER_TRANCHE, CARDS_PER_TRANCHE } from '../services/api';

export type BillingPeriod = 'monthly' | 'yearly';

export const toDisplayNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatStorageMb = (mb: number): string =>
  mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} Go` : `${Math.round(mb)} Mo`;

export interface PlanPricing {
  amount: number;
  suffix: string | null;
  billingPeriod: BillingPeriod;
}

export const getPlanPricing = (plan: any): PlanPricing => {
  if (plan.has_ads) {
    return { amount: 0, suffix: null, billingPeriod: 'monthly' };
  }
  const monthly = toDisplayNumber(plan.price_monthly);
  if (monthly > 0) {
    return { amount: monthly, suffix: '/ mois', billingPeriod: 'monthly' };
  }
  return { amount: toDisplayNumber(plan.price_yearly), suffix: '/ an', billingPeriod: 'yearly' };
};

/**
 * Périodicité de facturation à appliquer pour un plan donné.
 *
 * Règle : elle est déduite du plan lui-même (chaque offre Premium a un tarif
 * unique, donc une périodicité fixe). On ne se fie à `requested` que pour un
 * éventuel ancien plan à double tarif (mensuel > 0 ET annuel > 0). Miroir
 * front de SubscriptionPlan::normalizeBillingPeriod() côté backend.
 */
export const resolvePlanBillingPeriod = (plan: any, requested?: BillingPeriod): BillingPeriod => {
  const monthly = toDisplayNumber(plan.price_monthly);
  const yearly = toDisplayNumber(plan.price_yearly);
  if (monthly > 0 && yearly > 0) {
    return requested === 'yearly' || requested === 'monthly' ? requested : 'monthly';
  }
  return getPlanPricing(plan).billingPeriod;
};

const STALE_PLAN_FEATURE_PATTERNS = [
  /tableaux? inclus/i,
  /colonnes par tableau/i,
  /cartes? (par tableau|au total)/i,
  /invit[ée]s?.*tableau/i,
  /stockage inclus/i,
  /économie/i,
];

export const getPlanDisplayFeatures = (plan: any): string[] => {
  const computed: string[] = [];

  if (plan.max_boards != null) {
    computed.push(`${plan.max_boards} tableau${plan.max_boards > 1 ? 'x' : ''} inclus`);
  }
  if (plan.max_columns_per_board != null) {
    computed.push(`${plan.max_columns_per_board} colonnes par tableau`);
  }
  if (plan.max_cards_total != null) {
    computed.push(`${toDisplayNumber(plan.max_cards_total)} cartes incluses`);
  }
  if (plan.max_guests_per_board != null) {
    computed.push(`${toDisplayNumber(plan.max_guests_per_board)} invités par tableau`);
  }
  if (plan.max_storage_mb != null) {
    computed.push(`${formatStorageMb(toDisplayNumber(plan.max_storage_mb))} de stockage inclus`);
  }

  const customFeatures = Array.isArray(plan.features)
    ? plan.features.filter((f: unknown): f is string => typeof f === 'string' && !STALE_PLAN_FEATURE_PATTERNS.some((re) => re.test(f)))
    : [];

  return [...computed, ...customFeatures];
};

export const getPlanTagline = (plan: any): string => {
  const description = typeof plan.description === 'string' ? plan.description : '';
  const sentences = (description.match(/[^.!?]+[.!?]*/g) || [])
    .map((sentence: string) => sentence.trim())
    .filter((sentence: string) => sentence.length > 0);
  const withoutEconomyMentions = sentences.filter((sentence: string) => !/économi|%/i.test(sentence));
  const result = withoutEconomyMentions.join(' ').trim();
  return result.length > 0 ? result : description;
};

export interface PlanOverageRates {
  billingPeriod: BillingPeriod;
  periodSuffix: string;
  boardsPricePerTranche: number;
  boardsTrancheSize: number;
  cardsPricePerTranche: number;
  cardsTrancheSize: number;
  storagePricePerGb: number;
}

export const getPlanOverageRates = (plan: any): PlanOverageRates => {
  const { billingPeriod } = getPlanPricing(plan);
  const pick = (monthly: unknown, yearly: unknown): number =>
    toDisplayNumber(billingPeriod === 'yearly' ? yearly : monthly);

  return {
    billingPeriod,
    periodSuffix: billingPeriod === 'yearly' ? '/ an' : '/ mois',
    boardsPricePerTranche: pick(plan.price_per_10_extra_boards, plan.price_per_10_extra_boards_yearly),
    boardsTrancheSize: BOARDS_PER_TRANCHE,
    cardsPricePerTranche: pick(plan.price_per_1000_extra_cards, plan.price_per_1000_extra_cards_yearly),
    cardsTrancheSize: CARDS_PER_TRANCHE,
    storagePricePerGb: pick(plan.price_per_extra_gb, plan.price_per_extra_gb_yearly),
  };
};
